import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse, openAuthedFile } from "@/api/client";
import toast from "react-hot-toast";
import { formatDate } from "@ckgo2/shared/utils";
import type { User } from "@ckgo2/shared/types";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import {
  Mail,
  Phone,
  MessageSquare,
  MessageCircle,
  RefreshCw,
  Settings2,
  Send,
  Loader2,
  ShoppingCart,
  UserPlus,
  CheckCircle2,
  Sparkles,
  Paperclip,
  FileSearch,
  Globe,
} from "lucide-react";
import { useAgent } from "@/components/agent/AgentProvider";

interface InboxMsg {
  id: string;
  channel: string;
  direction: string;
  threadId?: string | null;
  subject?: string | null;
  content?: string | null;
  fromAddress?: string | null;
  toAddress?: string | null;
  isRead: boolean;
  meta?: {
    fromName?: string;
    snippet?: string;
    triage?: { intent?: string; confidence?: number };
    // Block 5.5 — AI extraction of a transcribed phone call (channel='phone').
    call?: {
      summary?: string;
      topics?: string[];
      callerName?: string;
      callerPhone?: string;
      followUpAt?: string;
      followUpNote?: string;
      followUpAppliedAt?: string;
    };
  } | null;
  leadId?: string | null;
  orderId?: string | null;
  customerId?: string | null;
  createdAt: string;
  leadNumber?: string | null;
  orderNumber?: string | null;
  assignedToName?: string | null;
}

interface FacetedInbox {
  data: InboxMsg[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

interface InboxConfig {
  mailboxes: string[];
  oauthClientConfigured: boolean;
}

const CHANNEL_ICON: Record<string, typeof Mail> = {
  email: Mail,
  instagram: MessageCircle,
  facebook: MessageCircle,
  webchat: MessageSquare,
  phone: Phone,
  web_inquiry: Globe,
  contact_form: MessageSquare,
};

const CHANNEL_LABEL: Record<string, string> = {
  email: "Email",
  instagram: "Instagram",
  facebook: "Facebook",
  webchat: "Web chat",
  phone: "Phone",
  web_inquiry: "Trip inquiry",
  contact_form: "Contact form",
};

// Per-channel colour for the inbox icon + chip — origin visible at a glance (E2E-009).
const CHANNEL_COLOR: Record<string, string> = {
  email: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  web_inquiry: "bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400",
  contact_form: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  instagram: "bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400",
  facebook: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  webchat: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
  phone: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
};
const DEFAULT_CHANNEL_COLOR =
  "bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400";

// AI intent-triage chip (Block 4.13 Řez 5) — colours the suggested intent on an un-triaged inbox item.
const TRIAGE_CHIP: Record<string, string> = {
  sales: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  question: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  supplier: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  noise: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
};

export default function InboxPage() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const qc = useQueryClient();
  const [showConfig, setShowConfig] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  // Returned from the Gmail OAuth consent flow — surface the result + refresh config.
  useEffect(() => {
    const status = searchParams.get("gmail");
    if (!status) return;
    setShowConfig(true);
    setSyncMsg(
      status === "connected"
        ? "Mailbox connected"
        : status === "config_error"
          ? "OAuth misconfigured: this API origin's redirect URI is not registered in the Google Cloud console."
          : "Could not connect mailbox",
    );
    qc.invalidateQueries({ queryKey: ["inbox-config"] });
    const url = new URL(window.location.href);
    url.searchParams.delete("gmail");
    window.history.replaceState({}, "", url.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["inbox", qs],
    queryFn: () => api.get<FacetedInbox>(`/api/admin/inbox?${qs}`),
  });
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const { data: configData } = useQuery({
    queryKey: ["inbox-config"],
    queryFn: () => api.get<ApiResponse<InboxConfig>>("/api/admin/inbox/config"),
  });

  const sync = useMutation({
    mutationFn: () => api.post<{ stored: number; mailboxes: number }>("/api/admin/inbox/sync"),
    onSuccess: (r) => {
      setSyncMsg(`Synced — ${r.stored} new message${r.stored === 1 ? "" : "s"}`);
      qc.invalidateQueries({ queryKey: ["inbox"] });
    },
    onError: (e: Error) => setSyncMsg(e.message || "Sync failed"),
  });

  const navigate = useNavigate();
  // 1-click "Create lead" from an inbox message (vision §5.2): Inbox = intake → Leads = worklist.
  const createLead = useMutation({
    mutationFn: (communicationId: string) =>
      api.post<ApiResponse<{ id: string }>>("/api/admin/leads/from-communication", {
        communicationId,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      if (r.data?.id) navigate(`/leads/${r.data.id}`);
    },
  });

  const config = configData?.data;
  const msgs = data?.data ?? [];
  const facets = data?.facets ?? {};
  const userName = (id: string) => usersData?.data.find((u) => u.id === id)?.name ?? id;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Inbox</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Unified messages — Gmail, social DMs, phone
          </p>
        </div>
        <div className="flex items-center gap-2">
          {syncMsg && <span className="text-sm text-gray-500">{syncMsg}</span>}
          <button onClick={() => setShowConfig((s) => !s)} className="btn-ghost">
            <Settings2 className="h-5 w-5" />
          </button>
          <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn-primary">
            {sync.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <RefreshCw className="h-5 w-5" />
            )}
            Sync now
          </button>
        </div>
      </div>

      {showConfig && <InboxConfigPanel config={config} />}

      <FacetedListLayout
        searchPlaceholder="Search messages…"
        total={data?.total ?? 0}
        limit={data?.limit ?? 30}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title="Channel"
              paramKey="channel"
              buckets={facets.channel ?? []}
              labelFor={(v) => CHANNEL_LABEL[v] ?? v}
            />
            <FacetPanel
              title="Agent"
              paramKey="assignedTo"
              buckets={facets.assignedTo ?? []}
              labelFor={userName}
              nullLabel="Unassigned"
            />
          </>
        }
      >
        <div className="card divide-y divide-gray-100 p-0 dark:divide-gray-700/60">
          {msgs.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <Mail className="mb-2 h-10 w-10 text-gray-300" />
              <p>No messages</p>
            </div>
          ) : (
            msgs.map((m) => {
              const Icon = CHANNEL_ICON[m.channel] ?? Mail;
              const channelColor = CHANNEL_COLOR[m.channel] ?? DEFAULT_CHANNEL_COLOR;
              const sender = m.meta?.fromName || m.fromAddress || "Unknown";
              return (
                <div
                  key={m.id}
                  className={m.isRead ? "" : "bg-primary-50/40 dark:bg-primary-900/10"}
                >
                  <button
                    onClick={() => setReplyTo(replyTo === m.id ? null : m.id)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <div
                      className={`${channelColor} mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium text-gray-900 dark:text-white">
                          {m.direction === "outbound" ? `To: ${m.toAddress}` : sender}
                        </span>
                        <span className="shrink-0 text-xs text-gray-400">
                          {formatDate(m.createdAt, { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="truncate text-sm text-gray-700 dark:text-gray-300">
                        {m.subject || "(no subject)"}
                      </p>
                      <p className="truncate text-xs text-gray-400">
                        {m.meta?.snippet || m.content}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${channelColor}`}
                        >
                          {CHANNEL_LABEL[m.channel] ?? m.channel}
                        </span>
                        {m.meta?.triage?.intent && !m.leadId && (
                          <span
                            className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                              TRIAGE_CHIP[m.meta.triage.intent] ?? "bg-gray-100 text-gray-600"
                            }`}
                            title={`AI triage: ${m.meta.triage.intent}${
                              m.meta.triage.confidence
                                ? ` (${Math.round(m.meta.triage.confidence * 100)}%)`
                                : ""
                            }`}
                          >
                            ✦ {m.meta.triage.intent}
                          </span>
                        )}
                        {m.leadId && (
                          <Link
                            to={`/leads/${m.leadId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 hover:underline dark:bg-gray-700 dark:text-gray-300"
                          >
                            <UserPlus className="h-3 w-3" /> {m.leadNumber}
                          </Link>
                        )}
                        {m.orderId && (
                          <Link
                            to={`/orders/${m.orderId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 hover:underline dark:bg-gray-700 dark:text-gray-300"
                          >
                            <ShoppingCart className="h-3 w-3" /> {m.orderNumber}
                          </Link>
                        )}
                        {!m.leadId && !m.orderId && !m.customerId && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            Unassigned
                          </span>
                        )}
                        {m.assignedToName && (
                          <span className="text-[11px] text-gray-400">· {m.assignedToName}</span>
                        )}
                      </div>
                    </div>
                  </button>

                  {replyTo === m.id && <MessageDetail id={m.id} call={m.meta?.call} />}

                  {replyTo === m.id && !m.leadId && !m.orderId && (
                    <div className="px-4 pb-3">
                      <button
                        type="button"
                        onClick={() => createLead.mutate(m.id)}
                        disabled={createLead.isPending}
                        className="btn-ghost btn-sm"
                      >
                        {createLead.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <UserPlus className="h-4 w-4" />
                        )}
                        Create lead
                      </button>
                    </div>
                  )}

                  {replyTo === m.id &&
                    ["email", "web_inquiry", "contact_form"].includes(m.channel) &&
                    m.fromAddress && (
                      <ReplyBox
                        to={m.direction === "outbound" ? m.toAddress! : m.fromAddress}
                        mailbox={
                          m.direction === "outbound"
                            ? (m.fromAddress ?? undefined)
                            : (m.toAddress ?? undefined)
                        }
                        subject={m.subject ? `Re: ${m.subject.replace(/^Re:\s*/i, "")}` : "Re:"}
                        threadId={m.threadId ?? undefined}
                        leadId={m.leadId ?? undefined}
                        orderId={m.orderId ?? undefined}
                        customerId={m.customerId ?? undefined}
                        incoming={m.content ?? m.meta?.snippet ?? undefined}
                        fromName={m.meta?.fromName ?? undefined}
                        onSent={() => {
                          setReplyTo(null);
                          qc.invalidateQueries({ queryKey: ["inbox"] });
                        }}
                      />
                    )}
                </div>
              );
            })
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}

function InboxConfigPanel({ config }: { config?: InboxConfig }) {
  const qc = useQueryClient();
  const [testResult, setTestResult] = useState<string | null>(null);

  // Connect a mailbox: fetch the Google consent URL, then redirect the browser to it.
  const connect = useMutation({
    mutationFn: () =>
      api.get<{ data: { url: string } }>(
        `/api/admin/integrations/gmail/connect?return=${encodeURIComponent(
          `${window.location.origin}/inbox`,
        )}`,
      ),
    onSuccess: (r) => {
      window.location.href = r.data.url;
    },
  });
  const disconnect = useMutation({
    mutationFn: (mb: string) =>
      api.post("/api/admin/integrations/gmail/disconnect", { mailbox: mb }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox-config"] }),
  });
  const test = useMutation({
    mutationFn: (mb: string) =>
      api.post<{ message: string }>("/api/admin/inbox/test", { mailbox: mb }),
    onSuccess: (r) => setTestResult(r.message || "OK"),
    onError: (e: Error) => setTestResult(e.message || "Failed"),
  });

  return (
    <div className="card space-y-4">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
        <Settings2 className="h-4 w-4" /> Inbox settings
      </h2>

      {config && !config.oauthClientConfigured && (
        <p className="text-sm text-amber-600">
          Google OAuth client not configured — set GDRIVE_CLIENT_ID / GDRIVE_CLIENT_SECRET.
        </p>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Connected mailboxes</p>
        {config?.mailboxes.length ? (
          config.mailboxes.map((mb) => (
            <div
              key={mb}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
            >
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm text-gray-900 dark:text-white">{mb}</span>
              <div className="ml-auto flex gap-2">
                <button onClick={() => test.mutate(mb)} className="btn-ghost btn-sm">
                  Test
                </button>
                <button
                  onClick={() => disconnect.mutate(mb)}
                  className="btn-ghost btn-sm text-red-600"
                >
                  Disconnect
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-400">No mailboxes connected yet.</p>
        )}
        {testResult && <p className="text-sm text-gray-500">{testResult}</p>}
      </div>

      <button
        onClick={() => connect.mutate()}
        disabled={!config?.oauthClientConfigured || connect.isPending}
        className="btn-primary disabled:opacity-50"
      >
        {connect.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Mail className="h-4 w-4" />
        )}
        Connect a mailbox
      </button>
    </div>
  );
}

interface MsgAttachment {
  filename?: string;
  mimeType?: string;
  size?: number;
}

// Loads a message's full body (R2-offloaded for long emails) + attachment chips, and offers an
// AI "Parse document" action that extracts supplier-doc fields (PNR/flight/policy) from the body
// — e.g. an airline/insurer confirmation. Shown when a message row is expanded. For a transcribed
// phone call (Block 5.5) it leads with the AI summary + the 1-click follow-up proposal.
function MessageDetail({ id, call }: { id: string; call?: NonNullable<InboxMsg["meta"]>["call"] }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["inbox-body", id],
    queryFn: () =>
      api.get<ApiResponse<{ body: string; attachments: MsgAttachment[] }>>(
        `/api/admin/inbox/${id}/body`,
      ),
  });
  const parse = useMutation({
    mutationFn: () =>
      api.post<ApiResponse<Record<string, string>>>(`/api/admin/inbox/${id}/parse`, {}),
  });
  const applyFollowUp = useMutation({
    mutationFn: () => api.post(`/api/admin/inbox/${id}/apply-follow-up`, {}),
    onSuccess: () => {
      toast.success("Follow-up scheduled");
      queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to apply follow-up"),
  });

  const body = data?.data.body ?? "";
  const attachments = data?.data.attachments ?? [];
  const parsed = parse.data?.data;
  const parsedFields = parsed
    ? Object.entries(parsed).filter(([, v]) => v && String(v).trim())
    : [];

  return (
    <div className="space-y-3 border-t border-gray-100 px-4 py-3 dark:border-gray-700/60">
      {call?.summary && (
        <div className="rounded-md bg-green-50 p-3 text-sm dark:bg-green-900/20">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-green-700 dark:text-green-400">
            ✦ Call summary
          </div>
          <p className="text-gray-700 dark:text-gray-300">{call.summary}</p>
          {(call.topics?.length ?? 0) > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {call.topics!.map((t) => (
                <span
                  key={t}
                  className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          {call.followUpAt && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-green-800 dark:text-green-300">
              <span>
                Proposed follow-up {call.followUpAt}
                {call.followUpNote ? ` — ${call.followUpNote}` : ""}
              </span>
              {call.followUpAppliedAt ? (
                <span className="font-medium">✓ Scheduled</span>
              ) : (
                <button
                  type="button"
                  onClick={() => applyFollowUp.mutate()}
                  disabled={applyFollowUp.isPending}
                  className="rounded bg-green-600 px-2 py-0.5 font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  Apply
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      ) : (
        body && (
          <div className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-800/40 dark:text-gray-300">
            {body}
          </div>
        )
      )}

      {attachments.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {attachments.map((att, idx) => (
            <button
              key={idx}
              onClick={() =>
                openAuthedFile(
                  `/api/admin/inbox/${id}/attachments/${idx}`,
                  att.filename || `attachment-${idx}`,
                )
              }
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
              title={att.mimeType || "attachment"}
            >
              <Paperclip className="h-3.5 w-3.5 text-gray-400" />
              <span className="max-w-[14rem] truncate">
                {att.filename || `Attachment ${idx + 1}`}
              </span>
            </button>
          ))}
        </div>
      )}

      <div>
        <button
          onClick={() => parse.mutate()}
          disabled={parse.isPending}
          className="text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium disabled:opacity-50"
        >
          {parse.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileSearch className="h-3.5 w-3.5" />
          )}
          Parse document
        </button>
        {parse.isError && (
          <p className="mt-1 text-xs text-red-600">{(parse.error as Error).message}</p>
        )}
        {parse.isSuccess &&
          (parsedFields.length > 0 ? (
            <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
              {parsedFields.map(([k, v]) => (
                <div key={k} className="flex gap-1">
                  <dt className="font-medium text-gray-500">{k}:</dt>
                  <dd className="break-all text-gray-800 dark:text-gray-200">{String(v)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-1 text-xs text-gray-400">No supplier-document fields found.</p>
          ))}
      </div>
    </div>
  );
}

function ReplyBox({
  to,
  mailbox,
  subject,
  threadId,
  leadId,
  orderId,
  customerId,
  incoming,
  fromName,
  onSent,
}: {
  to: string;
  /** Send FROM this mailbox (the one that received/sent the thread) — not always mailboxes[0]. */
  mailbox?: string;
  subject: string;
  threadId?: string;
  leadId?: string;
  orderId?: string;
  customerId?: string;
  incoming?: string;
  fromName?: string;
  onSent: () => void;
}) {
  const { enabled: agentEnabled } = useAgent();
  const [body, setBody] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () =>
      api.post("/api/admin/inbox/reply", {
        to,
        mailbox,
        subject,
        body,
        threadId,
        leadId,
        orderId,
        customerId,
      }),
    onSuccess: onSent,
  });

  // "Draft reply": single-shot /agent/draft grounded on the incoming message + entity, fills
  // the textarea for the operator to edit and send. Nothing is sent without an explicit Send.
  const draft = async () => {
    setDrafting(true);
    setDraftError(null);
    try {
      const entity = orderId
        ? `order ${orderId}`
        : leadId
          ? `inquiry ${leadId}`
          : customerId
            ? `customer ${customerId}`
            : "unknown contact";
      const context = [
        `Reply recipient: ${fromName ? `${fromName} <${to}>` : to}`,
        `Subject: ${subject}`,
        `Related entity: ${entity}`,
        incoming ? `\nCustomer's message:\n${incoming}` : "",
      ]
        .filter(Boolean)
        .join("\n");
      const res = await api.post<ApiResponse<{ text: string }>>("/api/admin/agent/draft", {
        task: "Draft a warm, concise, professional email reply to the customer's message below for a Vietnam travel agency. Address their question directly, keep our friendly brand tone, and do not invent prices, dates or availability that aren't given. Sign off as the Banh Mi Escape Travel team. Return only the email body.",
        context,
      });
      setBody(res.data.text);
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : "Couldn't draft a reply.");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700/60 dark:bg-gray-800/40">
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Reply to <span className="font-medium">{to}</span> · {subject}
        </div>
        {agentEnabled && (
          <button
            onClick={draft}
            disabled={drafting}
            className="text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium disabled:opacity-50"
          >
            {drafting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Draft reply
          </button>
        )}
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder="Write your reply…"
        className="input resize-none text-sm"
      />
      {draftError && <p className="text-xs text-red-600">{draftError}</p>}
      {send.isError && <p className="text-xs text-red-600">{(send.error as Error).message}</p>}
      <div className="flex justify-end">
        <button
          onClick={() => send.mutate()}
          disabled={!body.trim() || send.isPending}
          className="btn-primary btn-sm disabled:opacity-50"
        >
          {send.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Send
        </button>
      </div>
    </div>
  );
}
